import posthog, { type Properties } from 'posthog-js';

export type PostHogEvent =
  | 'login_attempted'
  | 'login_token_verified'
  | 'login_signed_out';

export function initPostHog(): void {
  const token = process.env.REACT_APP_POSTHOG_PROJECT_TOKEN;

  if (!token || posthog.__loaded) return;

  try {
    posthog.init(token, {
      api_host:
        process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com',
      defaults: '2026-05-30',
      person_profiles: 'identified_only',
      autocapture: true,
      session_recording: { maskAllInputs: false },
      debug: process.env.NODE_ENV !== 'production',
    });
  } catch {
    // Analytics failures must not interrupt authentication.
  }
}

export function captureEvent(
  event: PostHogEvent,
  properties?: Properties
): void {
  if (!posthog.__loaded) return;

  try {
    if (properties) {
      posthog.capture(event, properties);
    } else {
      posthog.capture(event);
    }
  } catch {
    // Analytics failures must not interrupt authentication.
  }
}

export function captureLoginAttempt(
  contact: string,
  contactType: string,
  success: boolean
): void {
  captureEvent('login_attempted', {
    component: 'login',
    contact,
    contact_type: contactType,
    success,
  });
}

export function captureTokenVerification(
  contactType: string,
  contact: string,
  success: boolean,
  error?: string
): void {
  const safeError = [
    'Invalid Token',
    'Token is expired',
    'Restart flow error',
    'Sign in/up not allowed',
  ].includes(error || '')
    ? error
    : undefined;

  captureEvent('login_token_verified', {
    component: 'token',
    contact_type: contactType,
    contact,
    success,
    error: safeError,
  });
}

export function captureSignOut(): void {
  if (!posthog.__loaded) return;

  captureEvent('login_signed_out', { component: 'signout' });

  try {
    posthog.reset();
  } catch {}
}
